import React, { useCallback, useEffect, useState } from "react";
import {
  Chip,
  CircularProgress,
  makeStyles,
  Tooltip
} from "@material-ui/core";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import { toast } from "react-toastify";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { i18n } from "../../translate/i18n";
import ConfirmationModal from "../ConfirmationModal";

const useStyles = makeStyles(theme => ({
  bar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: "6px 10px 2px",
    overflowX: "auto",
    overflowY: "hidden",
    WebkitOverflowScrolling: "touch",
    background: "rgba(0,0,0,0.03)",
    borderBottom: "1px solid rgba(0,0,0,0.06)",
    "&::-webkit-scrollbar": {
      height: 4
    },
    "&::-webkit-scrollbar-thumb": {
      background: "rgba(0,0,0,0.2)",
      borderRadius: 2
    }
  },
  icon: {
    color: theme.palette.text.secondary,
    fontSize: 18,
    flexShrink: 0,
    marginRight: 6
  },
  chip: {
    flexShrink: 0,
    maxWidth: 180,
    height: 28,
    marginRight: 6,
    fontSize: "0.75rem",
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    "&:hover": {
      backgroundColor: theme.palette.action.hover
    },
    "& .MuiChip-label": {
      paddingLeft: 8,
      paddingRight: 8,
      overflow: "hidden",
      textOverflow: "ellipsis"
    }
  },
  loading: {
    marginLeft: 4
  }
}));

const TicketChatFlowBar = ({ ticket, ticketStatus }) => {
  const classes = useStyles();
  const [flows, setFlows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [startingId, setStartingId] = useState(null);
  const [confirmFlow, setConfirmFlow] = useState(null);

  const ticketWhatsappId = ticket?.whatsappId;

  const loadFlows = useCallback(async () => {
    if (ticketStatus !== "open") {
      setFlows([]);
      return;
    }

    setLoading(true);
    try {
      const { data } = await api.get("/chat-flows");
      const list = Array.isArray(data) ? data : [];
      const active = list.filter(flow => {
        if (!flow?.active) return false;
        if (flow.whatsappId == null) return true;
        if (ticketWhatsappId == null) return false;
        return Number(flow.whatsappId) === Number(ticketWhatsappId);
      });
      setFlows(active);
    } catch (err) {
      setFlows([]);
    } finally {
      setLoading(false);
    }
  }, [ticketStatus, ticketWhatsappId]);

  useEffect(() => {
    loadFlows();
  }, [loadFlows]);

  const startFlow = async flow => {
    if (!ticket?.id || !flow?.id) return;

    const flowId = flow.id;
    setStartingId(flowId);
    try {
      await api.post(`/chat-flows/${flowId}/start`, {
        ticketId: Number(ticket.id)
      });
      toast.success(
        i18n.t("messagesInput.chatFlows.started", { name: flow.name })
      );
    } catch (err) {
      toastError(err);
    } finally {
      setStartingId(null);
    }
  };

  if (ticketStatus !== "open") {
    return null;
  }

  if (!loading && flows.length === 0) {
    return null;
  }

  return (
    <>
      <div className={classes.bar} role="toolbar" aria-label={i18n.t("messagesInput.chatFlows.label")}>
        <Tooltip title={i18n.t("messagesInput.chatFlows.label")}>
          <AccountTreeIcon className={classes.icon} />
        </Tooltip>
        {loading && flows.length === 0 ? (
          <CircularProgress size={16} className={classes.loading} />
        ) : (
          flows.map(flow => (
            <Chip
              key={flow.id}
              size="small"
              clickable
              disabled={Boolean(startingId)}
              className={classes.chip}
              label={
                startingId === flow.id
                  ? i18n.t("messagesInput.chatFlows.starting")
                  : flow.name
              }
              onClick={() =>
                setConfirmFlow({
                  id: flow.id,
                  name: flow.name
                })
              }
              color={startingId === flow.id ? "primary" : "default"}
              variant="outlined"
            />
          ))
        )}
      </div>
      <ConfirmationModal
        title={i18n.t("messagesInput.chatFlows.confirmTitle")}
        open={Boolean(confirmFlow)}
        onClose={() => setConfirmFlow(null)}
        onConfirm={() => {
          if (confirmFlow) {
            startFlow(confirmFlow);
          }
        }}
      >
        {i18n.t("messagesInput.chatFlows.confirmMessage", {
          name: confirmFlow?.name || ""
        })}
      </ConfirmationModal>
    </>
  );
};

export default TicketChatFlowBar;
